import "./index.less";
import React from "react";
import OrderDetails from "./OrderDetails";
import CheckboxColumn from "./column/CheckboxColumn";
import OriginalIdColumn from "./column/OriginalIdColumn";
import StatusColumn from "./column/StatusColumn";
import BuyerUsernameColumn from "./column/BuyerUsernameColumn";
import NewOrderItemsColumn from "./column/NewOrderItemsColumn";
import ConsigneeColumn from "./column/ConsigneeColumn";
import WaybillNoColumn from "./column/WaybillNoColumn";
import SingleOrderOperationColumn from "./column/SingleOrderOperationColumn";
import PaymentsColumn from "./column/PaymentsColumn";
import PaymentTimeColumn from "./column/PaymentTimeColumn";
import InvoiceInfoColumn from "./column/InvoiceInfoColumn";
import CustomizationInfoColumn from "./column/CustomizationInfoColumn";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import ShopDTO from "@/dto/shop/ShopDTO";
import { OrderType } from "@/scenes/OrderManagement/redux/states/TableState";
import SupplierOrderInfoColumn from "./column/dropShipping/SupplierOrderInfoColumn";
import RetailerInfoColumn from "./column/dropShipping/RetailerInfoColumn";
import { observer } from "mobx-react-lite";
import ShipperColumn from "./column/ShipperColumn";
import OrderTableColumnsType from "@/scenes/OrderManagement/enum/OrderTableColumnsType";
import ShopInfoColumn from "./column/ShopInfoColumn";
import OperationColumn from "./column/OperationColumn";
import ShopType from "@/enum/ShopType";
import ShippingDeadlineColumn from "./column/ShippingDeadlineColumn";
import OrderRemarkColumn from "./column/OrderRemarkColumn";
import LogisticsAgingPredictColumn from "./column/LogisticsAgingPredictColumn";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import SellerReceiptsColumn from "./column/SellerReceiptsColumn";
import OrderPrintInfo from "./OrderPrintInfo";
import LogisticsRecommendColumn from "./column/LogisticsRecommendColumn";
import classNames from "classnames";
import type { OrderTableColumn } from "@/scenes/OrderManagement/store/OrderTableStorageStore/utils/orderTableColumns";
import { getRenderableOrderTableColumns } from "../utils";

interface OrderRowProps {
    order: OrderType;

    tab: OrderTab;

    isQianniu?: boolean;

    shop: ShopDTO;

    isSupplierShop?: boolean;

    index?: number;

    visibleColumns?: OrderTableColumn[];
}

interface WrapOrderDetailProps {
    order: OrderType;
    isSupplierShop?: boolean;
    shopType: ShopType;
    colSpan: number;
}

const WrapOrderDetail: React.FC<WrapOrderDetailProps> = observer(function (props) {
    const { order, shopType, isSupplierShop, colSpan } = props;

    const orderManagementStore = useOrderManagementStore();

    const isShowAllOrderDetails = orderManagementStore.storage.isShowAllOrderDetails;

    const isShowOrderDetails = orderManagementStore.storage.showOrderDetailMapping.get(order.id);

    const isShowDetails = typeof isShowOrderDetails === "boolean" ? isShowOrderDetails : isShowAllOrderDetails;

    if (!isShowDetails) {
        return null;
    }

    return (
        <>
            <OrderDetails
                order={order}
                shopType={shopType}
                isSupplierShop={isSupplierShop}
                colSpan={colSpan}
            />

            <OrderPrintInfo order={order} colSpan={colSpan} />
        </>
    );
});

const OrderRow: React.FC<OrderRowProps> = ({
    order,
    tab,
    isQianniu,
    shop,
    isSupplierShop,
    index,
    visibleColumns: propVisibleColumns,
}) => {
    const orderManagementStore = useOrderManagementStore();

    const shopType = shop.type;

    const visibleColumns =
        propVisibleColumns || getRenderableOrderTableColumns(orderManagementStore.storage.sortedColumns, tab);
    const visibleColumnCount = visibleColumns.length || orderManagementStore.storage.visibleColumnCount;

    const tds: React.ReactNode[] = [];

    visibleColumns.forEach((column) => {
        const { type: key } = column;

        switch (key) {
            case OrderTableColumnsType.CHECKBOX:
                tds.push(<CheckboxColumn index={index} order={order} key={OrderTableColumnsType.CHECKBOX} />);
                break;
            case OrderTableColumnsType.STATUS:
                tds.push(<StatusColumn order={order} tab={tab} key={OrderTableColumnsType.STATUS} />);
                break;
            case OrderTableColumnsType.SHOP_INFO:
                tds.push(<ShopInfoColumn shopId={order.shopId} key={OrderTableColumnsType.SHOP_INFO} />);
                break;
            case OrderTableColumnsType.ORIGINAL_ID:
                tds.push(
                    <OriginalIdColumn order={order} shopType={shopType} key={OrderTableColumnsType.ORIGINAL_ID} />,
                );
                break;
            case OrderTableColumnsType.RETAILER:
                tds.push(<RetailerInfoColumn order={order} key={OrderTableColumnsType.RETAILER} />);
                break;
            case OrderTableColumnsType.BUYER_USERNAME:
                tds.push(
                    <BuyerUsernameColumn
                        order={order}
                        shopType={shopType}
                        key={OrderTableColumnsType.BUYER_USERNAME}
                    />,
                );
                break;
            case OrderTableColumnsType.PAYMENTS:
                tds.push(<PaymentsColumn order={order} key={OrderTableColumnsType.PAYMENTS} />);
                break;
            case OrderTableColumnsType.QUANTITY:
                tds.push(
                    <td key={OrderTableColumnsType.QUANTITY}>
                        <span className={order.quantity > 1 ? "text-danger font-bold font-size-110pct" : ""}>
                            {order.quantity}
                        </span>
                    </td>,
                );
                break;
            case OrderTableColumnsType.SELLER_RECEIPTS:
                tds.push(<SellerReceiptsColumn key={OrderTableColumnsType.SELLER_RECEIPTS} order={order} />);
                break;
            case OrderTableColumnsType.PRODUCT:
                tds.push(<NewOrderItemsColumn order={order} shopType={shopType} key={OrderTableColumnsType.PRODUCT} />);
                break;
            case OrderTableColumnsType.CUSTOMIZATION_INFO:
                tds.push(
                    <CustomizationInfoColumn order={order} key={OrderTableColumnsType.CUSTOMIZATION_INFO} />,
                );
                break;
            case OrderTableColumnsType.SHIPPING_DEADLINE:
                tds.push(
                    <ShippingDeadlineColumn
                        key={OrderTableColumnsType.SHIPPING_DEADLINE}
                        shop={shop}
                        shippingDeadline={order.shippingDeadline}
                        tab={tab}
                    />,
                );
                break;
            case OrderTableColumnsType.ORDER_TIME:
                tds.push(
                    <td key={OrderTableColumnsType.ORDER_TIME}>
                        <div>
                            {order.orderTime?.split(" ").map((str, index) => (
                                <p key={index}>{str}</p>
                            ))}
                        </div>
                    </td>,
                );
                break;
            case OrderTableColumnsType.PAYMENT_TIME:
                tds.push(<PaymentTimeColumn key={OrderTableColumnsType.PAYMENT_TIME} order={order} />);
                break;
            case OrderTableColumnsType.INVOICE_INFO:
                tds.push(<InvoiceInfoColumn order={order} key={OrderTableColumnsType.INVOICE_INFO} />);
                break;
            case OrderTableColumnsType.CONSIGNEE:
                tds.push(<ConsigneeColumn tab={tab} order={order} shop={shop} key={OrderTableColumnsType.CONSIGNEE} />);
                break;
            case OrderTableColumnsType.ORDER_REMARK:
                tds.push(<OrderRemarkColumn order={order} key={OrderTableColumnsType.ORDER_REMARK} />);
                break;
            case OrderTableColumnsType.SHIPPING_TIME:
                tds.push(
                    <td key={OrderTableColumnsType.SHIPPING_TIME}>
                        <div>
                            {order.shippingTime?.split(" ").map((str, index) => (
                                <p key={index}>{str}</p>
                            ))}
                        </div>
                    </td>,
                );
                break;
            case OrderTableColumnsType.WAYBILL_NO:
                tds.push(
                    <WaybillNoColumn
                        order={order}
                        rowIndex={index}
                        shopType={shopType}
                        key={OrderTableColumnsType.WAYBILL_NO}
                    />,
                );
                break;
            case OrderTableColumnsType.SUPPLIER_DROPSHIPPING2:
                tds.push(<SupplierOrderInfoColumn order={order} key={OrderTableColumnsType.SUPPLIER_DROPSHIPPING2} />);
                break;
            case OrderTableColumnsType.SEQUENCE_NO:
                tds.push(
                    <td key={OrderTableColumnsType.SEQUENCE_NO}>
                        <span>{order.sequenceNo}</span>
                    </td>,
                );
                break;
            case OrderTableColumnsType.WEIGHT:
                tds.push(
                    <td key={OrderTableColumnsType.WEIGHT}>
                        <span>{order.weight / 1000}</span>
                    </td>,
                );
                break;
            case OrderTableColumnsType.SHIPPER:
                tds.push(
                    <ShipperColumn
                        shippingAddressId={order.sellerRemarks?.shippingAddressId}
                        key={OrderTableColumnsType.SHIPPER}
                    />,
                );
                break;

            case OrderTableColumnsType.LOGISTICS_AGING_PREDICT:
                tds.push(
                    <LogisticsAgingPredictColumn
                        key={OrderTableColumnsType.LOGISTICS_AGING_PREDICT}
                        orderId={order.id}
                    />,
                );
                break;

            case OrderTableColumnsType.LOGISTICS_RECOMMEND:
                tds.push(
                    <LogisticsRecommendColumn key={OrderTableColumnsType.LOGISTICS_RECOMMEND} order={order} />,
                );
                break;
            case OrderTableColumnsType.QUICK_OPERATION:
                tds.push(
                    <SingleOrderOperationColumn
                        order={order}
                        tab={tab}
                        key={OrderTableColumnsType.QUICK_OPERATION}
                    />,
                );
                break;
            case OrderTableColumnsType.OPERATION:
                tds.push(
                    <OperationColumn
                        order={order}
                        tab={tab}
                        key={key}
                        isSupplierShop={isSupplierShop}
                        isQianniu={isQianniu}
                    />,
                );
                break;
            default:
                break;
        }
    });

    return (
        <tbody
            data-order-id={order.id}
            data-order-index={index}
            className={classNames({
                "order-row-wired-data": !!order._weirdInfo,
            })}
        >
            <tr>{tds}</tr>

            <WrapOrderDetail
                order={order}
                shopType={shopType}
                isSupplierShop={isSupplierShop}
                colSpan={visibleColumnCount}
            />
        </tbody>
    );
};

export default observer(OrderRow);
